import React, { useEffect, useRef, useState } from 'react'
import type { CurrentUser } from './api'
import { PageHeader } from '../PageHeader'
import { SiteFooter } from '../SiteFooter'
import { ThemeContextValue, tbClass, uiPrimitives, useThemedStyles } from '../theme'
import { displayName, emailLocalPart } from './auth'
import { Profile, clearAvatar, fetchProfile, saveNickname, uploadAvatar } from './profile'
import { CredentialSection } from './Credentials'

/**
 * 个人设置页 —— 编辑昵称与头像。
 *
 * 版面照编曲练习库那一套（PageHeader + useThemedStyles + uiPrimitives），不另起视觉。
 * 两个可改项：
 *   · 昵称：默认是邮箱 @ 前部分，改成自定义；公开曲谱在广场用它署名。
 *   · 头像：上传到后端本地磁盘（Django + nginx 直接送），公开可读。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	page: {
		padding: '24px 22px',
		[`@media (max-width: ${t.breakpoint}px)`]: {
			padding: '16px 12px',
		},
	},
	card: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
		padding: 22,
		maxWidth: 520,
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 22,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
	},
	/** 头像：圆形，默认显昵称/邮箱首字 */
	avatar: {
		flex: 'none',
		width: 72,
		height: 72,
		borderRadius: '50%',
		overflow: 'hidden',
		display: 'grid',
		placeItems: 'center',
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		fontFamily: t.fonts.serif,
		fontSize: 28,
		fontWeight: 600,
		border: `1px solid ${t.palette.line2}`,
		'& img': {
			width: '100%',
			height: '100%',
			objectFit: 'cover' as const,
		},
	},
	avatarActs: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 8,
	},
	hint: {
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1.6,
	},
	label: {
		display: 'block',
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
		marginBottom: 6,
	},
	input: {
		width: '100%',
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 14,
		padding: '9px 11px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper,
		color: t.palette.ink,
		outline: 'none',
		boxSizing: 'border-box' as const,
		'&:focus': {
			borderColor: t.palette.celadon,
			boxShadow: `0 0 0 3px ${t.palette.celadon2}`,
		},
	},
	acts: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
		flexWrap: 'wrap' as const,
	},
	err: {
		fontSize: 13,
		color: t.palette.cinnabar,
		lineHeight: 1.6,
	},
	ok: {
		fontSize: 13,
		color: t.palette.celadonInk,
	},
	email: {
		fontFamily: t.fonts.mono,
		fontSize: 12.5,
		color: t.palette.ink3,
	},
})


export function ProfilePage(props: {
	user: CurrentUser
	onHome: () => void
	onLogout: () => void
}) {
	const classes = useThemedStyles(stylesFactory)
	const [profile, setProfile] = useState<Profile | null>(null)
	// 昵称输入框的草稿值；savedNick 是已存值，用于判断「有无改动」
	const [draft, setDraft] = useState('')
	const [savedNick, setSavedNick] = useState('')
	const [busy, setBusy] = useState(false)
	const [msg, setMsg] = useState<{ok: boolean, text: string} | null>(null)
	const fileRef = useRef<HTMLInputElement | null>(null)

	useEffect(() => {
		let cancelled = false
		fetchProfile().then(p => {
			if (cancelled) return
			setProfile(p)
			const nick = p.nickname ?? ''
			setDraft(nick)
			setSavedNick(nick)
		})
		return () => { cancelled = true }
	}, [])

	// 展示名：已设昵称 > 邮箱前缀（与 displayName 同一套优先级）
	const shownName = displayName(props.user)
	const initial = (shownName || 'U').trim().charAt(0).toUpperCase()
	const defaultName = emailLocalPart(props.user)

	async function submitNickname() {
		setBusy(true)
		setMsg(null)
		try {
			await saveNickname(draft)
			setSavedNick(draft.trim())
			setMsg({ok: true, text: '昵称已保存'})
		} catch (err: any) {
			setMsg({ok: false, text: err?.message ?? '保存失败'})
		} finally {
			setBusy(false)
		}
	}

	async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		if (e.target) e.target.value = '' // 允许重复选同一文件
		if (!file) return
		setBusy(true)
		setMsg(null)
		try {
			const url = await uploadAvatar(file)
			setProfile(p => (p ? {...p, avatar_url: url} : p))
			setMsg({ok: true, text: '头像已更新'})
		} catch (err: any) {
			setMsg({ok: false, text: err?.message ?? '上传失败'})
		} finally {
			setBusy(false)
		}
	}

	async function onClearAvatar() {
		setBusy(true)
		setMsg(null)
		try {
			await clearAvatar()
			setProfile(p => (p ? {...p, avatar_url: null} : p))
			setMsg({ok: true, text: '头像已移除'})
		} catch (err: any) {
			setMsg({ok: false, text: err?.message ?? '操作失败'})
		} finally {
			setBusy(false)
		}
	}

	return <PageHeader
		doc="个人设置"
		onHome={props.onHome}
		actions={[{key: 'back', label: '返回编曲练习库', variant: 'outline' as const, onClick: props.onHome}]}
		trailing={<span style={{fontSize: 12.5}}>{shownName}</span>}
		onLogout={props.onLogout}>
		<div className={classes.page}>
			<input ref={fileRef} type="file" accept="image/*" style={{display: 'none'}} onChange={onPickFile} />
			<div className={classes.card}>
				{/* 头像 */}
				<div className={classes.row}>
					<div className={classes.avatar}>
						{profile?.avatar_url
							? <img src={profile.avatar_url} alt="头像" />
							: initial}
					</div>
					<div className={classes.avatarActs}>
						<button className={tbClass(classes, 'outline')} disabled={busy}
							onClick={() => fileRef.current?.click()}>更换头像</button>
						{profile?.avatar_url &&
							<button className={tbClass(classes)} disabled={busy} onClick={onClearAvatar}>移除</button>}
						<span className={classes.hint}>支持常见图片格式，不超过 3MB。</span>
					</div>
				</div>

				{/* 昵称 */}
				<div>
					<label className={classes.label} htmlFor="profile-nick">
						昵称（公开曲谱在广场用它署名）
					</label>
					<input className={classes.input} id="profile-nick" value={draft}
						placeholder={defaultName || '未设置昵称'}
						maxLength={24}
						onChange={e => setDraft(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && submitNickname()} />
					<p className={classes.hint} style={{marginTop: 6}}>
						留空则使用默认名「{defaultName || '用户'}」（邮箱 @ 前的部分）。
					</p>
				</div>

				{msg && <div className={msg.ok ? classes.ok : classes.err}>{msg.text}</div>}

				<div className={classes.acts}>
					<button className={tbClass(classes, 'outline')}
						disabled={busy || draft === savedNick}
						onClick={() => setDraft(savedNick)}>还原</button>
					<button className={tbClass(classes, 'primary')}
						disabled={busy || draft === savedNick}
						onClick={submitNickname}>
						{busy ? '保存中…' : '保存昵称'}
					</button>
				</div>

				{/* 登录方式：绑定 / 解绑。数据自己拉（/auth/identities），
				    不依赖 props.user —— 那样每次绑定完还得让上层重新取会话。 */}
				<CredentialSection />
			</div>
			<SiteFooter />
		</div>
	</PageHeader>
}
